import { useEffect, useMemo } from "react";
import { TFunction } from "i18next/typescript/t";
import * as yup from "yup";
import { useTranslation } from "react-i18next";
import { App } from "antd";
import { UploadFile } from "antd/es/upload/interface";
import { yupResolver } from "@hookform/resolvers/yup";
import { useForm } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";

import {
  useLazyGetCountriesQuery,
  useUpdateUserByIdMutation,
  useLazyGetAchievementsQuery,
  useLazyGetCommunityRolesQuery,
  useLazyGetAreasOfExpertiseQuery,
} from "@/redux/service.js";
import {
  HookFormBaseDropdown,
  HookFormBaseUpload,
  HookFormInfiniteScrollDropdown,
  HookFormInput,
  HookFormCheckbox,
  HookFormInfiniteScrollCustomChoiceDropdown,
} from "@/components_v2/FormFields/ReactHookForm/fields";
import {
  Honorifics,
  Roles,
  SUPPORTED_PICTURE_FILE_FORMATS,
  URL_REGEX,
} from "@/shared/utils/constants";
import {
  CountryResponse,
  CountryQueryParams,
  CommunityRoleResponse,
  AchievementResponse,
  AchievementQueryParams,
  CommunityRoleQueryParams,
} from "@/redux/common/types";
import FormFieldWrapper from "@/components_v2/Wrapper/FormFieldWrapper";
import { handleErrors } from "@/shared/utils/forms";
import BaseButton from "@/components_v2/Button/BaseButton";
import BaseModal from "@/components_v2/Modal/BaseModal";
import BaseSvgRenderer from "@/components_v2/Svg/BaseSvgRenderer";
import DeleteIcon from "@/assets/icons/delete.svg?react";
import { UserDetailsFormProps, UserFormValues } from "../types";
import {
  ExpertiseAreaQueryParams,
  ExpertiseAreaResponse,
  FullUserResponse,
} from "@/redux/users/types";
import { setUser } from "@/redux/authentication/reducer";
import { selectCurrentUser } from "@/redux/authentication/selectors";
import {
  BUTTON_SIZES,
  BUTTON_VARIANTS,
} from "@/components_v2/Button/constants";

import "../styles.css";

const updateUserSchema = (
  currentUser: FullUserResponse,
  t: TFunction,
): yup.ObjectSchema<UserFormValues> =>
  yup.object().shape({
    firstName: yup
      .string()
      .typeError(t("form.validation.notValid"))
      .required(t("form.validation.required")),
    lastName: yup
      .string()
      .typeError(t("form.validation.notValid"))
      .required(t("form.validation.required")),
    profilePicture: yup
      .mixed<UploadFile>()
      .test("fileType", t("form.validation.notValid"), (value?: UploadFile) => {
        if (!value || !value.type) {
          return true;
        }
        return SUPPORTED_PICTURE_FILE_FORMATS.includes(value.type);
      })
      .nullable(),
    isEmailHidden: yup.boolean(),
    profile: yup.object().shape({
      honorific: yup
        .string()
        .test(
          "honorificType",
          t("form.validation.notValid"),
          (value?: string) => {
            if (!value) {
              return true;
            }
            return Honorifics.getValues().includes(value);
          },
        )
        .typeError(t("form.validation.notValid")),
      jobTitle: yup
        .string()
        .typeError(t("form.validation.notValid"))
        .when([], {
          is: () => currentUser.role === Roles.ADMIN.value,
          then: (schema) => schema.optional(),
          otherwise: (schema) => schema.required(t("form.validation.required")),
        }),
      department: yup
        .string()
        .typeError(t("form.validation.notValid"))
        .when([], {
          is: () => currentUser.role === Roles.ADMIN.value,
          then: (schema) => schema.optional(),
          otherwise: (schema) => schema.required(t("form.validation.required")),
        }),
      city: yup
        .string()
        .typeError(t("form.validation.notValid"))
        .when([], {
          is: () => currentUser.role === Roles.ADMIN.value,
          then: (schema) => schema.optional(),
          otherwise: (schema) => schema.required(t("form.validation.required")),
        }),
      country: yup
        .number()
        .typeError(t("form.validation.notValid"))
        .when([], {
          is: () => currentUser.role === Roles.ADMIN.value,
          then: (schema) => schema.optional(),
          otherwise: (schema) => schema.required(t("form.validation.required")),
        }),
      website: yup
        .string()
        .test(
          "websiteInvalidUrl",
          t("form.validation.invalidUrl"),
          (value?: string) => {
            if (!value) {
              return true;
            }
            return URL_REGEX.test(value);
          },
        ),
      linkedIn: yup
        .string()
        .test(
          "linkedinInvalidUrl",
          t("form.validation.invalidUrl"),
          (value?: string) => {
            if (!value) {
              return true;
            }
            return URL_REGEX.test(value);
          },
        ),
      achievements: yup.array(
        yup
          .number()
          .required(t("form.validation.required"))
          .typeError(t("form.validation.notValid")),
      ),
      communityRoles: yup.array(
        yup
          .number()
          .required(t("form.validation.required"))
          .typeError(t("form.validation.notValid")),
      ),
      areasOfExpertise: yup.array(
        yup
          .mixed<number | string>()
          .test(
            "is-area-of-expertise-or-int",
            t("users.errors.notValid"),
            function (value) {
              if (
                Number.isInteger(Number(value)) &&
                String(Number(value)) === String(value)
              ) {
                return true;
              }

              return yup.string().isValidSync(value);
            },
          )
          .required(t("form.validation.required"))
          .typeError(t("form.validation.notValid")),
      ),
    }),
  });

const UserProfileDetailsForm = ({
  user,
  open,
  setOpen,
}: UserDetailsFormProps) => {
  const { t } = useTranslation();
  const { notification } = App.useApp();
  const dispatch = useDispatch();

  const currentUser = useSelector(selectCurrentUser);
  const [updateUser] = useUpdateUserByIdMutation();
  const [getCountries] = useLazyGetCountriesQuery();
  const [getAchievements] = useLazyGetAchievementsQuery();
  const [getAreasOfExpertise] = useLazyGetAreasOfExpertiseQuery();
  const [getCommunityRoles] = useLazyGetCommunityRolesQuery();

  const form = useForm<UserFormValues>({
    resolver: (values, context, options) => {
      if (!currentUser) {
        return Promise.resolve({
          values: {},
          errors: {
            root: {
              type: "manual",
              message: "Loading user data...",
            },
          },
        });
      }
      return yupResolver(updateUserSchema(currentUser, t))(
        values,
        context,
        options,
      );
    },
    mode: "onChange",
    defaultValues: {
      firstName: "",
      lastName: "",
      profilePicture: null,
      isEmailHidden: false,
      profile: {
        honorific: "",
        jobTitle: "",
        department: "",
        city: "",
        country: undefined,
        website: "",
        linkedIn: "",
        achievements: [],
        communityRoles: [],
        areasOfExpertise: [],
      },
    },
  });

  const formProfilePicture = form.watch("profilePicture");

  const currentProfilePicture = useMemo(() => {
    const uploadedProfilePicture = formProfilePicture;

    if (
      uploadedProfilePicture &&
      typeof uploadedProfilePicture === "object" &&
      uploadedProfilePicture instanceof File
    ) {
      return URL.createObjectURL(uploadedProfilePicture);
    }

    // If form value is explicitly null, return null (image was removed)
    if (uploadedProfilePicture === null) {
      return null;
    }

    return user.profilePicture || null;
  }, [formProfilePicture, user.profilePicture]);

  useEffect(() => {
    if (open && user) {
      form.reset(
        {
          firstName: user.firstName,
          lastName: user.lastName,
          profilePicture: null,
          isEmailHidden: user.isEmailHidden,
          profile: {
            honorific: user.profile.honorific,
            jobTitle: user.profile.jobTitle,
            department: user.profile.department,
            city: user.profile.city,
            country: user.profile.country || undefined,
            website: user.profile.website,
            linkedIn: user.profile.linkedIn,
            achievements: user.profile.achievements,
            communityRoles: user.profile.communityRoles,
            areasOfExpertise: user.profile.areasOfExpertise,
          },
        },
        { keepDefaultValues: true },
      );
    }
  }, [open, user]);

  const fields = useMemo(() => {
    if (!currentUser) return [];

    return [
      {
        type: "group",
        fields: [
          {
            label: t("users.labels.city"),
            required: true,
            field: (
              <HookFormInput
                name="profile.city"
                required
                placeholder={t("users.labels.enterCity")}
                control={form.control}
              />
            ),
          },
          {
            label: t("users.labels.country"),
            required: true,
            field: (
              <HookFormInfiniteScrollDropdown<
                CountryResponse,
                CountryQueryParams
              >
                name="profile.country"
                control={form.control}
                fetchFn={getCountries}
                labelFieldName="name"
                valueFieldName="id"
                searchFieldName="name"
                placeholder={t("users.placeholders.selectCountry")}
              />
            ),
          },
        ],
      },
      {
        type: "group",
        fields: [
          {
            label: t("users.labels.jobTitle"),
            required: true,
            field: (
              <HookFormInput
                name="profile.jobTitle"
                required
                placeholder={t("users.placeholders.enterJobTitle")}
                control={form.control}
              />
            ),
          },
          {
            label: t("users.labels.department"),
            required: true,
            field: (
              <HookFormInput
                name="profile.department"
                required
                placeholder={t("users.labels.enterDepartment")}
                control={form.control}
              />
            ),
          },
        ],
      },
      {
        type: "single",
        label: t("users.labels.website"),
        required: false,
        field: (
          <HookFormInput
            name="profile.website"
            placeholder={t("users.placeholders.enterWebsite")}
            control={form.control}
          />
        ),
      },
      {
        type: "single",
        label: t("users.labels.linkedin"),
        required: false,
        field: (
          <HookFormInput
            name="profile.linkedIn"
            placeholder={t("users.placeholders.enterLinkedin")}
            control={form.control}
          />
        ),
      },
      {
        type: "single",
        label: t("users.labels.areasOfExpertise"),
        required: false,
        field: (
          <HookFormInfiniteScrollCustomChoiceDropdown<
            ExpertiseAreaResponse,
            ExpertiseAreaQueryParams
          >
            name="profile.areasOfExpertise"
            fetchFn={getAreasOfExpertise}
            labelFieldName="name"
            valueFieldName="id"
            searchFieldName="name"
            multiple
            placeholder={t("users.placeholders.selectAreasOfExpertise")}
            control={form.control}
            customChoiceOptions={{
              schema: yup
                .string()
                .required(t("form.validation.required"))
                .typeError(t("form.validation.notValid")),
              placeholder: t(
                "users.placeholders.enterAreaOfExpertise",
              ) as string,
            }}
          />
        ),
      },
      ...(currentUser.role === Roles.ADMIN.value
        ? [
            {
              type: "single",
              label: t("users.labels.achievements"),
              required: false,
              field: (
                <HookFormInfiniteScrollDropdown<
                  AchievementResponse,
                  AchievementQueryParams
                >
                  name="profile.achievements"
                  fetchFn={getAchievements}
                  labelFieldName="label"
                  valueFieldName="id"
                  searchFieldName="label"
                  multiple
                  placeholder={t("users.placeholders.selectAchievements")}
                  control={form.control}
                  defaultFilters={{
                    type: "profile",
                  }}
                />
              ),
            },
            {
              type: "single",
              label: t("users.labels.communityRoles"),
              required: false,
              field: (
                <HookFormInfiniteScrollDropdown<
                  CommunityRoleResponse,
                  CommunityRoleQueryParams
                >
                  name="profile.communityRoles"
                  fetchFn={getCommunityRoles}
                  labelFieldName="label"
                  valueFieldName="id"
                  searchFieldName="label"
                  multiple
                  placeholder={t("users.placeholders.selectCommunityRoles")}
                  control={form.control}
                  defaultFilters={{
                    type: "profile",
                  }}
                />
              ),
            },
          ]
        : []),
    ];
  }, [currentUser]);

  const handleSubmit = (values: UserFormValues) => {
    const processedValues = { ...values };

    const hasNewProfilePicture =
      values.profilePicture && values.profilePicture instanceof File;
    const profilePictureRemoved = values.profilePicture === null;

    if (!hasNewProfilePicture && !profilePictureRemoved) {
      delete processedValues.profilePicture;
    }

    if (profilePictureRemoved) {
      processedValues.profilePicture = "";
    }

    updateUser({ id: user.id, data: processedValues })
      .unwrap()
      .then((response) => {
        notification.success({
          message: t("users.labels.profileUpdated"),
        });
        if (currentUser && user.id === currentUser.id) {
          dispatch(setUser(response));
        }
        setOpen(false);
      })
      .catch((errors) => handleErrors(errors, form.setError, notification));
  };

  const handleCancel = () => {
    form.reset();
    setOpen(false);
  };

  const handleRemoveProfilePicture = () => {
    form.setValue("profilePicture", null);
  };

  return (
    <BaseModal
      open={open}
      onCancel={handleCancel}
      className="user-form-modal"
      width={800}
      title={t("users.labels.editProfileInfo")}
      footer={
        <div className="flex gap-200 justify-between items-center">
          <BaseButton
            key="cancel"
            variant={BUTTON_VARIANTS.SECONDARY}
            onClick={handleCancel}
          >
            {t("buttons.cancel")}
          </BaseButton>
          <BaseButton
            key="submit"
            variant={BUTTON_VARIANTS.PRIMARY}
            onClick={form.handleSubmit(handleSubmit)}
          >
            {t("buttons.save")}
          </BaseButton>
        </div>
      }
    >
      <div className="flex flex-col gap-300">
        <div className="flex gap-300 items-start">
          <div className="profile-picture-upload-container">
            <div
              className={`profile-picture-upload-circle ${currentProfilePicture ? "hide-overlay-elements" : ""}`}
              style={{
                backgroundImage: currentProfilePicture
                  ? `url(${currentProfilePicture})`
                  : "none",
              }}
            >
              <HookFormBaseUpload
                name="profilePicture"
                control={form.control}
                shouldShowValues={false}
                className="profile-picture-upload"
              />
            </div>

            {currentProfilePicture && (
              <BaseButton
                size={BUTTON_SIZES.SMALL}
                variant={BUTTON_VARIANTS.DANGER}
                leadingIcon={
                  <BaseSvgRenderer
                    component={DeleteIcon}
                    width="20"
                    height="20"
                    stroke="var(--base-white)"
                  />
                }
                onClick={handleRemoveProfilePicture}
                className="profile-picture-remove-btn"
              />
            )}
          </div>

          <div className="flex-1 flex flex-col gap-300">
            <div className="flex gap-200 items-start">
              <FormFieldWrapper
                label={t("users.labels.honorific")}
                required={false}
              >
                <HookFormBaseDropdown
                  className="honorific-dropdown"
                  name="profile.honorific"
                  placeholder={t("users.placeholders.selectHonorific")}
                  options={Honorifics.asList()}
                  control={form.control}
                  allowClear
                />
              </FormFieldWrapper>

              <FormFieldWrapper label={t("users.labels.firstName")} required>
                <HookFormInput
                  className="user-form-input-min-width"
                  name="firstName"
                  required
                  placeholder={t("users.placeholders.enterFirstName")}
                  control={form.control}
                />
              </FormFieldWrapper>

              <FormFieldWrapper label={t("users.labels.lastName")} required>
                <HookFormInput
                  className="user-form-input-min-width"
                  name="lastName"
                  required
                  placeholder={t("users.placeholders.enterLastsName")}
                  control={form.control}
                />
              </FormFieldWrapper>
            </div>

            <HookFormCheckbox
              name="isEmailHidden"
              label={t("users.labels.hideEmail")}
              control={form.control}
            />
          </div>
        </div>

        {fields.map((field, index) => {
          if (field.type === "group") {
            return (
              <div key={`group-${index + 2}`} className="flex gap-300">
                {field.fields?.map((groupField) => (
                  <FormFieldWrapper
                    key={groupField.label}
                    label={groupField.label}
                    required={groupField.required}
                  >
                    {groupField.field}
                  </FormFieldWrapper>
                ))}
              </div>
            );
          } else {
            return (
              <FormFieldWrapper
                key={`single-${index + 2}`}
                label={field.label || ""}
                required={field.required}
              >
                {field.field}
              </FormFieldWrapper>
            );
          }
        })}
      </div>
    </BaseModal>
  );
};

export default UserProfileDetailsForm;
